import type { ReactNode } from 'react'

/**
 * Briques de formulaire partagées.
 *
 * Elles existent pour que le RTL ne dépende pas de la discipline de chaque écran (invariant 11) :
 * la mise en page vient d'ici et du CSS, qui n'emploie que des propriétés logiques. Un écran qui
 * poserait son propre `margin-left` casserait l'arabe sans que rien ne le signale.
 */

interface ChampProps {
  id: string
  libelle: string
  type?: string
  valeur: string
  aide?: string
  requis?: boolean
  autoComplete?: string
  /**
   * Une unité affichée après le champ — « MAD », « mois », « min ». Portée par le champ et non par
   * le libellé : « Ancienneté (mois) » se lit deux fois, « Ancienneté » puis « 26 | mois » une.
   */
  suffixe?: string
  /** Occupe toute la largeur d'un formulaire à colonnes. */
  pleineLargeur?: boolean
  onChange: (valeur: string) => void
}

export function Champ({
  id,
  libelle,
  type = 'text',
  valeur,
  aide,
  requis,
  autoComplete,
  suffixe,
  pleineLargeur = false,
  onChange,
}: ChampProps) {
  const idAide = aide ? `${id}-aide` : undefined

  const saisie = (
    <input
      id={id}
      type={type}
      value={valeur}
      required={requis}
      autoComplete={autoComplete}
      aria-describedby={idAide}
      onChange={(evenement) => onChange(evenement.target.value)}
    />
  )

  return (
    <p className={pleineLargeur ? 'champ pleine-largeur' : 'champ'}>
      <label htmlFor={id}>{libelle}</label>
      {suffixe === undefined ? (
        saisie
      ) : (
        <span className="champ__groupe">
          {saisie}
          <span className="champ__suffixe">{suffixe}</span>
        </span>
      )}
      {aide && (
        <span className="aide" id={idAide}>
          {aide}
        </span>
      )}
    </p>
  )
}

interface ZoneProps {
  id: string
  libelle: string
  valeur: string
  aide?: string
  lignes?: number
  pleineLargeur?: boolean
  onChange: (valeur: string) => void
}

export function Zone({ id, libelle, valeur, aide, lignes = 4, pleineLargeur = false, onChange }: ZoneProps) {
  const idAide = aide ? `${id}-aide` : undefined

  return (
    <p className={pleineLargeur ? 'champ pleine-largeur' : 'champ'}>
      <label htmlFor={id}>{libelle}</label>
      <textarea
        id={id}
        rows={lignes}
        value={valeur}
        aria-describedby={idAide}
        onChange={(evenement) => onChange(evenement.target.value)}
      />
      {aide && (
        <span className="aide" id={idAide}>
          {aide}
        </span>
      )}
    </p>
  )
}

interface ListeProps {
  id: string
  libelle: string
  valeur: string
  options: readonly { valeur: string; libelle: string }[]
  aide?: string
  pleineLargeur?: boolean
  onChange: (valeur: string) => void
}

export function Liste({ id, libelle, valeur, options, aide, pleineLargeur = false, onChange }: ListeProps) {
  const idAide = aide ? `${id}-aide` : undefined

  return (
    <p className={pleineLargeur ? 'champ pleine-largeur' : 'champ'}>
      <label htmlFor={id}>{libelle}</label>
      <select
        id={id}
        value={valeur}
        aria-describedby={idAide}
        onChange={(evenement) => onChange(evenement.target.value)}
      >
        {options.map((option) => (
          <option key={option.valeur} value={option.valeur}>
            {option.libelle}
          </option>
        ))}
      </select>
      {aide && (
        <span className="aide" id={idAide}>
          {aide}
        </span>
      )}
    </p>
  )
}

/**
 * Bandeau de message.
 *
 * `role="alert"` sur l'erreur, `role="status"` sur le reste : sans cela, un lecteur d'écran
 * n'annonce pas l'échec d'un formulaire, et l'utilisateur reste devant un bouton qui « ne fait
 * rien ». C'est l'accessibilité WCAG 2.1 AA visée par le §13 de la spec, pas un détail.
 *
 * `attention` est le ton d'un geste à conséquence — confirmer un créneau lève le masquage — qui
 * n'est ni une erreur ni une information : il se lit AVANT le bouton, jamais après.
 */
export function Bandeau({
  ton,
  icone,
  children,
}: {
  ton: 'erreur' | 'succes' | 'info' | 'attention'
  icone?: ReactNode
  children: ReactNode
}) {
  return (
    <p className={`bandeau bandeau-${ton}`} role={ton === 'erreur' ? 'alert' : 'status'}>
      {icone}
      <span>{children}</span>
    </p>
  )
}

/**
 * Champ avec suggestions issues des données — conception L3, §1.1.
 *
 * **C'est la seule des trois dispositions du §1.1 qui agisse à la source.** La colonne normalisée et
 * la facette réparent après coup ; celle-ci empêche la divergence de naître. Chaque bien saisi sans
 * elle est une orthographe de plus à rapprocher le jour où les polygones seront chargés.
 *
 * `<datalist>` plutôt qu'une liste déroulante maison : le champ **reste libre** — c'est la décision
 * du 01/09/2026, et un quartier absent du catalogue doit rester saisissable — tout en proposant ce
 * qui existe déjà. Le navigateur s'occupe du clavier, du tactile et du lecteur d'écran.
 */
export function ChampSuggere({
  id,
  libelle,
  valeur,
  aide,
  requis,
  suggestions,
  onChange,
}: {
  id: string
  libelle: string
  valeur: string
  aide?: string
  requis?: boolean
  suggestions: string[]
  onChange: (valeur: string) => void
}) {
  const idAide = aide ? `${id}-aide` : undefined
  const idListe = `${id}-suggestions`

  return (
    <p className="champ">
      <label htmlFor={id}>{libelle}</label>
      <input
        id={id}
        type="text"
        value={valeur}
        required={requis}
        list={idListe}
        autoComplete="off"
        aria-describedby={idAide}
        onChange={(evenement) => onChange(evenement.target.value)}
      />
      <datalist id={idListe}>
        {suggestions.map((suggestion) => (
          <option key={suggestion} value={suggestion} />
        ))}
      </datalist>
      {aide && (
        <span className="aide" id={idAide}>
          {aide}
        </span>
      )}
    </p>
  )
}
